// Registry of the cross-context (and cross-lane) service calls frozen as typed stubs in
// `packages/domain-<context>/src/<dir>/public.ts` [BUILD-PLAN §4 rule 4, §5; 02 BR-ARC-008].
// Each entry's stub is `export const <name> = asyncStub<<signature>>('<context>.<dir>.<name>')`.

/** Bounded contexts with a domain package [02 §5.1]; planning is a read model. */
export const DOMAIN_CONTEXTS = [
  'masters',
  'partners',
  'product',
  'materials',
  'costing',
  'inventory',
  'purchasing',
  'sales',
  'production',
  'shipping',
  'planning',
  'd2c',
  'accounting',
] as const;

export type DomainContext = (typeof DOMAIN_CONTEXTS)[number];

/** `src/<dir>/**` owned by later lanes (derived from lanes.json `owns`); index.ts re-exports each. */
export const DOMAIN_DIRS: Readonly<Record<DomainContext, readonly string[]>> = Object.freeze({
  masters: Object.freeze(['masters']),
  partners: Object.freeze(['partners']),
  product: Object.freeze(['styles', 'specs-bom', 'development', 'dpp']),
  materials: Object.freeze(['fabric-trim', 'lots-quality']),
  costing: Object.freeze(['costing']),
  inventory: Object.freeze(['ledger', 'warehouse']),
  purchasing: Object.freeze(['purchasing', 'eta-freight', 'sourcing']),
  sales: Object.freeze(['customer-codes', 'po-parsers', 'programmes', 'orders', 'po-import']),
  production: Object.freeze(['cutting', 'sewing-fg', 'capacity']),
  shipping: Object.freeze(['labels', 'shipping', 'shipping-docs', 'notes']),
  planning: Object.freeze(['planning']),
  d2c: Object.freeze(['d2c-sync', 'd2c-orders']),
  accounting: Object.freeze([
    'gl',
    'ap',
    'ar-bank',
    'posting',
    'sage-bridge',
    'close',
    'finance-c',
  ]),
});

export interface CrossContextCall {
  readonly context: DomainContext;
  readonly dir: string;
  readonly name: string;
  /** Exported function type in the same public.ts. */
  readonly signature: string;
  /** Calling contexts, or `<context>/<dir>` for same-context cross-lane calls, or an app. */
  readonly consumers: readonly string[];
  readonly specRefs: readonly string[];
}

const call = (
  context: DomainContext,
  dir: string,
  name: string,
  signature: string,
  consumers: readonly string[],
  specRefs: readonly string[],
): CrossContextCall =>
  Object.freeze({
    context,
    dir,
    name,
    signature,
    consumers: Object.freeze([...consumers]),
    specRefs: Object.freeze([...specRefs]),
  });

export const CROSS_CONTEXT_CALLS: readonly CrossContextCall[] = Object.freeze([
  // masters
  call(
    'masters',
    'masters',
    'fxRateAt',
    'FxRateAt',
    ['costing', 'purchasing', 'sales', 'accounting'],
    ['04 F-MD-01', 'BR-MD-050'],
  ),
  call(
    'masters',
    'masters',
    'crossRate',
    'CrossRate',
    ['costing', 'accounting'],
    ['04 F-MD-02', 'F-COST-01'],
  ),
  call(
    'masters',
    'masters',
    'convertQty',
    'ConvertQty',
    ['product', 'materials', 'costing', 'inventory'],
    ['04 F-MD-05', '05 BR-MAT-001'],
  ),
  call('masters', 'masters', 'dueDates', 'DueDates', ['accounting'], ['04 F-MD-03']),
  // partners
  call(
    'partners',
    'partners',
    'contractorRateAt',
    'ContractorRateAt',
    ['costing', 'production'],
    ['04 F-PTN-05', 'W2b.costing public: partners rates'],
  ),
  call(
    'partners',
    'partners',
    'getPartnerSummary',
    'GetPartnerSummary',
    ['sales', 'shipping', 'accounting'],
    ['02 BR-ARC-008 [assumption: document snapshots]'],
  ),
  // product
  call(
    'product',
    'styles',
    'getSkuSnapshot',
    'GetSkuSnapshot',
    ['sales', 'shipping', 'd2c'],
    ['06 BR-SHP-012'],
  ),
  call(
    'product',
    'specs-bom',
    'getReleasedBom',
    'GetReleasedBom',
    ['costing', 'production'],
    ['W2b.costing public: product BOM', 'MB D9'],
  ),
  call(
    'product',
    'development',
    'evaluateReadiness',
    'EvaluateReadiness',
    ['production', 'sales', 'shipping'],
    ['04 F-DEV-01', 'BR-DEV-004 (G9)'],
  ),
  call('product', 'dpp', 'dppReadiness', 'DppReadiness', ['apps/api'], ['04 F-DPP-01']),
  // materials
  call(
    'materials',
    'fabric-trim',
    'priceAt',
    'PriceAt',
    ['costing', 'purchasing'],
    ['05 §17', 'W2b.costing public: materials priceAt'],
  ),
  call(
    'materials',
    'lots-quality',
    'createLotsFromReceipt',
    'CreateLotsFromReceipt',
    ['purchasing'],
    ['W2b.purchasing scope'],
  ),
  call(
    'materials',
    'lots-quality',
    'lotUsability',
    'LotUsability',
    ['inventory'],
    ['05 BR-INV-002', '02 G6'],
  ),
  // costing
  call(
    'costing',
    'costing',
    'isStyleOrderable',
    'IsStyleOrderable',
    ['sales'],
    ['02 G1', 'W2c.orders public: costing.isStyleOrderable'],
  ),
  call(
    'costing',
    'costing',
    'getApprovedMaterialPrices',
    'GetApprovedMaterialPrices',
    ['apps/api (purchasing tolerance)'],
    ['04 BR-COST-015'],
  ),
  call(
    'costing',
    'costing',
    'approvedUnitCost',
    'ApprovedUnitCost',
    ['production'],
    ['07 BR-PST-003', 'P11'],
  ),
  // inventory ledger
  call(
    'inventory',
    'ledger',
    'postMovement',
    'PostMovement',
    ['purchasing', 'production', 'shipping', 'd2c'],
    ['W1.ledger scope', '05 BR-INV-001'],
  ),
  call(
    'inventory',
    'ledger',
    'postMovements',
    'PostMovements',
    ['purchasing', 'production', 'shipping', 'd2c'],
    ['05 BR-INV-001 inventory.postMovements(tx, …)'],
  ),
  call(
    'inventory',
    'ledger',
    'reserve',
    'Reserve',
    ['sales', 'production', 'd2c'],
    ['05 §17', 'BR-INV-003'],
  ),
  call(
    'inventory',
    'ledger',
    'releaseReservation',
    'ReleaseReservation',
    ['sales', 'production', 'd2c'],
    ['05 §17', 'BR-INV-006'],
  ),
  call(
    'inventory',
    'ledger',
    'issueReserved',
    'IssueReserved',
    ['production', 'shipping', 'd2c'],
    ['05 §17', 'BR-INV-007'],
  ),
  // CR-BG2d (W2d.shipping R-LED-SHP-1): the dispatch issue of a shipment's reservations, all-or-nothing.
  call(
    'inventory',
    'ledger',
    'issueReservedMany',
    'IssueReservedMany',
    ['shipping'],
    ['05 §17', 'BR-INV-007', '06 BR-SHP-012'],
  ),
  call(
    'inventory',
    'ledger',
    'returnToStock',
    'ReturnToStock',
    ['production', 'shipping', 'd2c'],
    ['05 §17'],
  ),
  call(
    'inventory',
    'ledger',
    'availableAt',
    'AvailableAt',
    ['sales', 'production', 'planning', 'd2c'],
    ['05 §17 availableAt(item, wh, lot?, roll?)'],
  ),
  call(
    'inventory',
    'ledger',
    'convertQty',
    'LedgerConvertQty',
    ['purchasing', 'production'],
    ['05 §17', 'W1.ledger scope'],
  ),
  call('inventory', 'ledger', 'suggestLot', 'SuggestLot', ['production'], ['05 BR-INV-004']),
  // purchasing
  call(
    'purchasing',
    'purchasing',
    'createDraftsFromSuggestions',
    'CreateDraftsFromSuggestions',
    ['planning'],
    ['05 BR-PLN-013', 'W2c.planning public: purchasing drafts'],
  ),
  call(
    'purchasing',
    'eta-freight',
    'importEtaFeed',
    'ImportEtaFeed',
    ['purchasing/sourcing'],
    ['W2g.sourcing public: eta-freight import'],
  ),
  // sales
  call(
    'sales',
    'customer-codes',
    'resolveSize',
    'ResolveSize',
    ['sales/po-import', 'shipping'],
    ['04 F-SIZE-01', 'T40'],
  ),
  call(
    'sales',
    'customer-codes',
    'checkCustomerSkus',
    'CheckCustomerSkus',
    ['shipping'],
    ['02 G3'],
  ),
  call(
    'sales',
    'po-parsers',
    'parsePo',
    'ParsePo',
    ['sales/po-import'],
    ['06 §3.4', 'W2d.po-import public: po-parsers'],
  ),
  call(
    'sales',
    'programmes',
    'getApprovedWorksheet',
    'GetApprovedWorksheet',
    ['sales/po-import'],
    ['W2d.po-import public: programmes'],
  ),
  call(
    'sales',
    'orders',
    'createDraftFromImport',
    'CreateDraftFromImport',
    ['sales/po-import'],
    ['W2c.orders scope'],
  ),
  call(
    'sales',
    'orders',
    'getOrderLineMatrices',
    'GetOrderLineMatrices',
    ['production', 'shipping'],
    ['W2d.cutting / W2d.shipping public: orders'],
  ),
  // production
  call(
    'production',
    'cutting',
    'getCutPool',
    'GetCutPool',
    ['production/sewing-fg'],
    ['06 §14 GET /cutting-slips/{id}/pool', 'W2e.sewing-fg public: cutting pool'],
  ),
  // shipping
  call(
    'shipping',
    'labels',
    'evaluateMarketLabel',
    'EvaluateMarketLabel',
    ['shipping/shipping'],
    ['02 G4', 'W2d.shipping public: labels.evaluateMarketLabel'],
  ),
  call(
    'shipping',
    'shipping',
    'getShipmentDocumentData',
    'GetShipmentDocumentData',
    ['shipping/shipping-docs'],
    ['W2e.shipping-docs public: shipping'],
  ),
  // accounting
  call(
    'accounting',
    'gl',
    'createAutoJournal',
    'CreateAutoJournal',
    ['accounting/posting', 'accounting/sage-bridge', 'accounting/close'],
    ['07 BR-PST-001', 'W2d.posting public: gl journals'],
  ),
  call(
    'accounting',
    'gl',
    'getPeriodStatus',
    'GetPeriodStatus',
    ['accounting/ap', 'accounting/ar-bank', 'accounting/posting'],
    ['02 G7', '07 §3'],
  ),
  call(
    'accounting',
    'posting',
    'handleOutboxEvent',
    'HandleOutboxEvent',
    ['apps/worker'],
    ['07 BR-PST-001/002'],
  ),
]);
